/**
 * Boardova — B2B registration page.
 *
 * The form itself is rendered client-side by the Shopify Forms app, inside a
 * shadow root we do not control, with hashed class names. Everything below is
 * an overlay on top of it:
 *
 *   - restyles the app's widget so it matches the theme (fonts, inputs, buttons)
 *   - inserts the two group headings the form builder cannot express
 *   - relabels the "Edrpou" field and localises the app's validation copy
 *
 * Rule of thumb: never move nodes the app owns — the app re-renders and loses
 * track of them (that is what pushed the delivery-field validation messages to
 * the bottom of the form). Only add our own nodes and our own styles.
 */
(function () {
  'use strict';

  var FORM = '[data-testid="form"]';

  /* Copy ----------------------------------------------------------------- */

  /* Every string we render comes from the theme's locale files, handed over
     by the section as a JSON payload. Nothing is hardcoded here, so a missing
     translation means we leave that part of the form untouched rather than
     printing Ukrainian on the English page. */
  var i18n = readStrings();

  function readStrings() {
    var tag = document.querySelector('script[data-b2b-form-i18n]');
    if (!tag) return {};

    try {
      return JSON.parse(tag.textContent) || {};
    } catch (error) {
      if (window.console) console.warn('[b2b-form] could not read translations', error);
      return {};
    }
  }

  function t(group, key) {
    var value = (i18n[group] || {})[key];
    if (typeof value !== 'string') return '';

    value = value.trim();
    // Shopify renders an unknown key as "Translation missing: …".
    return value.indexOf('Translation missing') === 0 ? '' : value;
  }

  /* Field groups --------------------------------------------------------- */

  /* Each group is anchored on its first field. The app's data-testid values
     come from field keys the merchant can rename in the form builder, so we
     also match on the visible label — whichever resolves first wins. The
     labels are lookup keys, not copy: they list how the field is spelled in
     every language the form exists in. */
  var GROUPS = [
    {
      key: 'requisites',
      testids: ['field-custom#ffop_tov', 'field-custom#fop_tov', 'field-custom#ffop-tov'],
      labels: ['назва фоп / тов', 'назва фоп/тов', 'назва фоп або тов', 'sole proprietor / llc name']
    },
    {
      key: 'delivery',
      testids: ['field-custom#shipping_city'],
      labels: ['місто доставки', 'місто', 'delivery city', 'city']
    }
  ];

  /* The app validates in English whatever the storefront locale is. These are
     its own strings, used to look up our translation — not copy of ours. */
  var VALIDATION_SOURCES = {
    'field is required': 'required',
    'this field is required': 'required',
    'please fill out this field': 'required',
    'invalid email': 'email',
    'invalid email address': 'email',
    'please enter a valid email': 'email',
    'please enter a valid email address': 'email',
    'invalid phone number': 'phone',
    'please enter a valid phone number': 'phone',
    'please select an option': 'select'
  };

  /* Its built-in fields name themselves — "First name is required", "Email is
     invalid" — so the table above can never be complete. Match the shape
     instead, and let the field the message sits under say which one it is. */
  function validationKey(text) {
    if (VALIDATION_SOURCES[text]) return VALIDATION_SOURCES[text];
    // Long enough to be prose is long enough not to be a validation message.
    if (!text || text.length > 60) return '';

    if (/ is required$/.test(text)) return 'required';

    if (/(^| )(is invalid|invalid|is not valid)( |$)/.test(text)) {
      if (text.indexOf('email') !== -1) return 'email';
      if (text.indexOf('phone') !== -1) return 'phone';
      return 'invalid';
    }

    return '';
  }

  /* Field formats -------------------------------------------------------- */

  /* The Forms app can only mark a field required — it has no format or length
     rules of its own, so "aaaa" passed as a phone number. Each rule below is
     matched to a field by data-testid first, then by its label, and adds both
     a maxlength the browser enforces while typing and a check on blur and on
     submit. Empty is never an error here: required-ness stays the app's job. */
  var FIELD_RULES = [
    {
      key: 'phone',
      testids: ['field-custom#legal_phone', 'field-custom#shipping_phone'],
      labels: ['номер телефону', 'телефон отримувача', 'телефон одержувача', 'phone number'],
      attributes: { maxlength: '20', inputmode: 'tel' },
      test: function (value) {
        var count = digits(value).length;
        return /^\+?[\d\s()-]+$/.test(value.trim()) && count >= 9 && count <= 15;
      }
    },
    {
      key: 'edrpou',
      testids: ['field-custom#ederpu'],
      labels: ['єдрпоу', 'edrpou', 'єдрпоу / ірн'],
      attributes: { maxlength: '10', inputmode: 'numeric' },
      test: function (value) {
        var trimmed = value.trim();
        return /^\d+$/.test(trimmed) && (trimmed.length === 8 || trimmed.length === 10);
      }
    },
    {
      key: 'iban',
      testids: ['field-custom#iban'],
      labels: ['iban', 'іban'],
      attributes: { maxlength: '34', autocapitalize: 'characters', spellcheck: 'false' },
      // Typing an IBAN in groups of four is normal, so tidy it up rather than
      // rejecting it.
      tidy: function (value) {
        return value.replace(/\s+/g, '').toUpperCase();
      },
      test: isIban
    },
    {
      key: 'mfo',
      testids: ['field-custom#mfo'],
      labels: ['мфо', 'mfo'],
      attributes: { maxlength: '6', inputmode: 'numeric' },
      test: function (value) {
        return /^\d{6}$/.test(value.trim());
      }
    },
    {
      key: 'website',
      testids: ['field-custom#website', 'field-custom#shop_website'],
      labels: ['вебсайт магазину (якщо є)', 'вебсайт магазину', 'вебсайт', 'website', 'shop website'],
      attributes: { maxlength: '200', inputmode: 'url' },
      test: function (value) {
        return /^(https?:\/\/)?[\w-]+(\.[\w-]+)+(\/[^\s]*)?$/i.test(value.trim());
      }
    }
  ];

  function digits(value) {
    return value.replace(/\D/g, '');
  }

  /* Structure plus the ISO 7064 mod-97 checksum, so a transposed digit is
     caught here instead of by the accountant. */
  function isIban(value) {
    var iban = value.replace(/\s+/g, '').toUpperCase();
    if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false;

    var rearranged = iban.slice(4) + iban.slice(0, 4);
    var remainder = 0;

    for (var i = 0; i < rearranged.length; i++) {
      var code = rearranged.charCodeAt(i);
      var chunk = code >= 65 ? String(code - 55) : rearranged.charAt(i);
      for (var j = 0; j < chunk.length; j++) {
        remainder = (remainder * 10 + Number(chunk.charAt(j))) % 97;
      }
    }

    return remainder === 1;
  }

  /* Styles --------------------------------------------------------------- */

  /* `scope` is ":host" when the stylesheet goes into the app's shadow root —
     there the whole tree is ours to style, which is what the thank-you screen
     needs: the app swaps the form out for it, so anything hung off
     [data-testid="form"] stops applying the moment the form is submitted. */
  function css(scope) {
    var root = scope || FORM;

    var groupRules = GROUPS.map(function (group) {
      return (
        FORM + ' [data-b2b-group-heading="' + group.key + '"] + * {' +
        '  margin-block-start: 0 !important;' +
        '}'
      );
    }).join('');

    return [
      /* Bug: the app ships its own font stack, so the page read as a foreign
         widget. Blunt on purpose — the app's class names are hashed and change
         between releases, so we cannot enumerate them. */
      root + ', ' + root + ' * {',
      '  font-family: var(--text-font-family) !important;',
      '  letter-spacing: var(--text-letter-spacing) !important;',
      '}',
      root + ' {',
      '  color: rgb(var(--text-color, var(--text-primary))) !important;',
      '}',

      /* Inputs */
      FORM + ' [class*="_formInputField_"],',
      FORM + ' [class*="_formPhoneInputField_"],',
      FORM + ' [class*="_textArea_"],',
      FORM + ' [class*="_selectToggle_"] {',
      '  font-size: var(--text-base) !important;',
      '  border: 1px solid rgb(var(--border-color)) !important;',
      '  border-radius: var(--rounded-input) !important;',
      '  background: transparent !important;',
      '  transition: border-color .15s ease-in-out !important;',
      '}',
      FORM + ' [class*="_formInputField_"]:focus,',
      FORM + ' [class*="_formPhoneInputField_"]:focus,',
      FORM + ' [class*="_textArea_"]:focus,',
      FORM + ' [class*="_selectToggle_"]:focus-visible {',
      '  border-color: currentColor !important;',
      '  outline: none !important;',
      '  box-shadow: inset 0 0 0 1px !important;',
      '}',
      FORM + ' [class*="_formInputFieldLabel_"],',
      FORM + ' [class*="_label_"] {',
      '  color: rgb(var(--text-color, var(--text-primary)) / .6) !important;',
      '  background: transparent !important;',
      '}',
      FORM + ' [class*="_formInputFieldLabel_"]::before,',
      FORM + ' [class*="_formInputFieldLabel_"]::after,',
      FORM + ' [class*="_label_"]::before,',
      FORM + ' [class*="_label_"]::after,',
      FORM + ' [class*="_formFieldContainer_"]::before,',
      FORM + ' [class*="_formFieldContainer_"]::after {',
      '  background: transparent !important;',
      '  background-color: transparent !important;',
      '}',

      /* Labels inside inputs. The app centres a resting label with an inline
         `top` it measures once, at render — before our font has applied, so a
         label that wraps onto two lines in our font (the Instagram one on a
         phone) is measured as one line and sits low. Centre it in CSS
         instead. Once it floats up there is no room for two lines above the
         value, so it stays on one and gets cut with an ellipsis. Textareas
         keep the app's layout: their label sits at the top on purpose. */
      FORM + ' [class*="_formFieldContainer_"]:not(:has(textarea)):not(:focus-within) > label[class*="_formInputFieldLabel_"]:not([class*="_inputFilled_"]) {',
      '  top: 50% !important;',
      '  transform: translateY(-50%) !important;',
      '}',
      FORM + ' [class*="_formFieldContainer_"]:not(:has(textarea)):focus-within > label[class*="_formInputFieldLabel_"],',
      FORM + ' [class*="_formFieldContainer_"]:not(:has(textarea)) > label[class*="_inputFilled_"] {',
      '  top: calc(50% - .5em) !important;',
      '}',
      FORM + ' [class*="_formFieldContainer_"]:not(:has(textarea)):focus-within > label[class*="_formInputFieldLabel_"]:not(:has([data-b2b-hint])),',
      FORM + ' [class*="_formFieldContainer_"]:not(:has(textarea)) > label[class*="_inputFilled_"]:not(:has([data-b2b-hint])) {',
      '  white-space: nowrap !important;',
      '  overflow: hidden !important;',
      '  text-overflow: ellipsis !important;',
      '}',

      /* The country-code toggle sits next to the phone input and has to line
         up with it. */
      FORM + ' [class*="_formPhoneInputContainer_"] [class*="_selectToggle_"] {',
      '  min-height: 51px !important;',
      '}',

      /* Submit button */
      FORM + ' [class*="_formSubmitButton_"] {',
      '  font-weight: bold !important;',
      '  border-radius: var(--rounded-button) !important;',
      '  transition: background-color .15s ease-in-out, opacity .15s ease-in-out !important;',
      '}',
      FORM + ' [class*="_formSubmitButton_"]:hover {',
      '  opacity: .85 !important;',
      '}',

      /* Checkboxes. The fieldset keeps a few pixels of padding so the focus
         ring is not clipped when tabbing onto the first checkbox. */
      FORM + ' [class*="_checkboxFieldSet_"] {',
      '  border: none !important;',
      '  padding: 3px !important;',
      '  margin: 0 !important;',
      '  overflow: visible !important;',
      '}',
      FORM + ' [class*="_checkboxLegend_"] {',
      '  color: rgb(var(--text-color, var(--text-primary))) !important;',
      '  font-size: var(--text-base) !important;',
      '  font-weight: 500 !important;',
      '  margin-bottom: .625rem !important;',
      '  padding: 0 !important;',
      '}',
      FORM + ' [class*="_verticalLayout_"] {',
      '  display: flex !important;',
      '  flex-direction: column !important;',
      '  gap: .625rem !important;',
      '  overflow: visible !important;',
      '}',
      FORM + ' [class*="_checkboxField_"] {',
      '  display: flex !important;',
      '  align-items: center !important;',
      '  gap: .625rem !important;',
      '  cursor: pointer !important;',
      '  margin: 0 !important;',
      '  overflow: visible !important;',
      '}',
      FORM + ' [class*="_checkboxWrapper_"] {',
      '  display: flex !important;',
      '  align-items: center !important;',
      '  overflow: visible !important;',
      '}',
      FORM + ' [class*="_checkboxField_"] [class*="_textBody_"] {',
      '  color: rgb(var(--text-color, var(--text-primary))) !important;',
      '  font-size: var(--text-base) !important;',
      '}',
      FORM + ' [class*="_checkboxInput_"] {',
      '  -webkit-appearance: none !important;',
      '  appearance: none !important;',
      '  width: 18px !important;',
      '  height: 18px !important;',
      '  margin: 0 !important;',
      '  border: 1px solid rgb(var(--border-color)) !important;',
      '  border-radius: 4px !important;',
      '  background: transparent !important;',
      '  cursor: pointer !important;',
      '  flex-shrink: 0 !important;',
      '  position: relative !important;',
      '  transition: background-color .15s, border-color .15s !important;',
      '}',
      FORM + ' [class*="_checkboxInput_"]:checked {',
      '  background: rgb(var(--accent)) !important;',
      '  border-color: rgb(var(--accent)) !important;',
      '}',
      FORM + ' [class*="_checkboxInput_"]:focus-visible {',
      '  outline: 2px solid rgb(var(--accent)) !important;',
      '  outline-offset: 1px !important;',
      '}',
      FORM + ' [class*="_checkboxInput_"]:checked::after {',
      '  content: "" !important;',
      '  position: absolute !important;',
      '  left: 5px !important;',
      '  top: 2px !important;',
      '  width: 4px !important;',
      '  height: 8px !important;',
      '  border: solid #fff !important;',
      '  border-width: 0 2px 2px 0 !important;',
      '  transform: rotate(45deg) !important;',
      '}',

      /* Group headings we insert ourselves. */
      FORM + ' [data-b2b-group-heading] {',
      '  font-family: var(--heading-font-family) !important;',
      '  font-weight: bold !important;',
      '  font-size: var(--text-md, 1rem) !important;',
      '  line-height: 1.4 !important;',
      '  color: rgb(var(--text-color, var(--text-primary))) !important;',
      '  margin: 2rem 0 .75rem !important;',
      '  padding-block-start: 1.5rem !important;',
      '  border-block-start: 1px solid rgb(var(--border-color)) !important;',
      '}',
      groupRules,

      /* Field hints. On desktop the hint sits in the label, next to the field
         name, on one unwrapped line — wrapped, it would run over the typed
         value. While the label is a placeholder it is full size and the line
         is wider than the input, so the hint shrinks; once the label floats
         up (focus or a value) the app scales it down and the hint can go back
         to full size. On a phone even that is too wide, so the hint moves to
         its own line above the field instead, indented by the input's own
         inline padding (15px) so it lines up with the label inside. */
      FORM + ' [data-b2b-hint="inline"] {',
      '  font-size: .85em !important;',
      '}',
      FORM + ' [class*="_formInputField_"]:focus + label [data-b2b-hint="inline"],',
      FORM + ' label[class*="_inputFilled_"] [data-b2b-hint="inline"] {',
      '  font-size: 1em !important;',
      '}',
      FORM + ' label:has([data-b2b-hint="inline"]) {',
      '  white-space: nowrap !important;',
      '  max-width: none !important;',
      '}',
      FORM + ' [data-b2b-hint="above"] {',
      '  display: none !important;',
      '}',

      /* Our own field errors, sitting where the app puts its own — under the
         field, in the red the app block is configured with. */
      FORM + ' [data-b2b-field-error] {',
      '  margin: .375rem 0 .75rem !important;',
      '  font-size: var(--text-sm, .875rem) !important;',
      '  line-height: 1.4 !important;',
      '  color: #f83a3a !important;',
      '  text-align: start !important;',
      '}',

      /* Mobile: the app collapses its two-column rows into one, which left the
         groups running into each other. */
      '@media screen and (max-width: 749px) {',
      '  ' + FORM + ' [class*="_formFieldContainer_"] {',
      '    margin-block-end: 1rem !important;',
      '  }',
      '  ' + FORM + ' [data-b2b-group-heading] {',
      '    margin-block-start: 1.5rem !important;',
      '    padding-block-start: 1.5rem !important;',
      '  }',
      '  ' + FORM + ' [data-b2b-hint="inline"] {',
      '    display: none !important;',
      '  }',
      '  ' + FORM + ' [data-b2b-hint="above"] {',
      '    display: block !important;',
      // The form puts a .75rem gap between rows; eat most of it so the hint
      // reads as part of the field below, not of the one above.
      '    margin: 0 0 -.375rem !important;',
      '    padding-inline: 15px !important;',
      '    font-size: var(--text-sm, .875rem) !important;',
      '    line-height: 1.4 !important;',
      '    color: rgb(var(--text-color, var(--text-primary)) / .6) !important;',
      '    text-align: start !important;',
      '  }',
      '}'
    ].join('\n');
  }

  /* Helpers -------------------------------------------------------------- */

  var seenRoots = new WeakSet();
  var muted = false; // true while we mutate, so the observer ignores our own work

  function styleHost(root) {
    return root === document ? document.head : root;
  }

  /* We walk every shadow root on the page, so before scoping styles to :host
     we have to be sure the root is the Forms widget and not some other app's.
     The widget may also be remounted on the thank-you screen, without the form
     in it — hence the memory of roots that have held it. */
  var appRoots = new WeakSet();

  function isAppRoot(root) {
    if (root.querySelector(FORM) || root.querySelector('[data-testid^="form"]')) {
      appRoots.add(root);
      return true;
    }

    return appRoots.has(root);
  }

  function injectStyles(root) {
    var host = styleHost(root);
    if (!host) return;
    if (host.querySelector('style[data-boardova-b2b-styles]')) return;

    // In a shadow root we can scope to :host and keep styling the widget after
    // it has replaced the form with the thank-you screen. In the document we
    // have to stay narrowly on the form, so wait for it to exist.
    var shadow = root !== document;
    if (shadow ? !isAppRoot(root) : !root.querySelector(FORM)) return;

    var style = document.createElement('style');
    style.setAttribute('data-boardova-b2b-styles', '');
    style.textContent = css(shadow ? ':host' : null);
    host.appendChild(style);
  }

  /* Label text as the app renders it, reduced to something comparable:
     lowercase, no required marker, single spaces. */
  function normalise(text) {
    return (text || '')
      .replace(/[*\u00a0]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  /* The group's first field, by data-testid if we recognise it, otherwise by
     the label the merchant typed in the form builder. */
  function findAnchor(form, group) {
    var byTestId = null;
    group.testids.some(function (testid) {
      byTestId = form.querySelector('[data-testid="' + testid + '"]');
      return !!byTestId;
    });
    if (byTestId) return byTestId;

    var labels = form.querySelectorAll('label, [class*="_formInputFieldLabel_"], [class*="_label_"]');
    for (var i = 0; i < labels.length; i++) {
      if (group.labels.indexOf(normalise(labels[i].textContent)) === -1) continue;

      var id = labels[i].getAttribute('for');
      var field = id ? form.querySelector('[id="' + id + '"]') : null;
      return field || labels[i];
    }

    return null;
  }

  /* Insert a heading before the row that holds the group's first field.
     We only ever add our own node — the app's nodes stay exactly where the
     app put them. */
  function insertGroupHeadings(root) {
    var form = root.querySelector(FORM);
    if (!form) return;

    GROUPS.forEach(function (group) {
      var field = findAnchor(form, group);
      if (!field) {
        // Renaming the field in the form builder changes both its data-testid
        // and its label, so say which group lost its anchor.
        if (window.console) console.warn('[b2b-form] no anchor field for group "' + group.key + '"');
        return;
      }

      var row = field.closest('[class*="_formFieldContainer_"]') || field;
      while (row.parentElement && row.parentElement !== form) row = row.parentElement;
      if (row.parentElement !== form) return;

      var text = t('groups', group.key);
      if (!text) return;

      var previous = row.previousElementSibling;
      if (previous && previous.getAttribute('data-b2b-group-heading') === group.key) {
        if (previous.textContent !== text) previous.textContent = text;
        return;
      }

      var existing = form.querySelector('[data-b2b-group-heading="' + group.key + '"]');
      if (existing) existing.remove();

      var heading = document.createElement('div');
      heading.setAttribute('data-b2b-group-heading', group.key);
      heading.textContent = text;
      form.insertBefore(heading, row);
    });
  }

  /* The app has no translations of its own, so the "Edrpou" label and its
     built-in validation copy stay English whatever the storefront locale. */
  function localiseText(root) {
    var form = root.querySelector(FORM);
    if (!form) return;

    var edrpou = t('labels', 'edrpou');
    if (edrpou) {
      var input = form.querySelector('[data-testid="field-custom#ederpu"]');
      var labelId = input && input.getAttribute('aria-labelledby');
      var label = labelId && root.getElementById ? root.getElementById(labelId) : null;
      if (label && label.textContent.trim() !== edrpou && normalise(label.textContent) === 'edrpou') {
        label.textContent = edrpou;
      }
    }

    // Matching on the text rather than on a class: the app's class names are
    // hashed, so "_formFieldError_" today is something else after a release.
    // The form is small, and this only runs on a debounced rescan.
    var nodes = form.querySelectorAll('*');
    Array.prototype.forEach.call(nodes, function (node) {
      if (node.children.length || node.hasAttribute('data-b2b-field-error')) return;

      var source = validationKey(normalise(node.textContent).replace(/[.!]+$/, ''));
      var text = source && t('validation', source);
      if (text && text !== node.textContent.trim()) node.textContent = text;
    });
  }

  /* The email doubles as the login for the B2B account, which the form builder
     has no place to say. Both nodes are ours: a span appended to the app's
     label (React only rewrites its own text node, not ours) and a line before
     the field container. CSS picks one per breakpoint. */
  function insertHints(root) {
    const form = root.querySelector(FORM);
    if (!form) return;

    const text = t('hints', 'email');
    const input = form.querySelector('[data-testid="field-email"]');
    if (!text || !input) return;

    const labelId = input.getAttribute('aria-labelledby');
    const label = labelId && root.getElementById ? root.getElementById(labelId) : null;
    if (label) {
      let inline = label.querySelector('[data-b2b-hint="inline"]');
      if (!inline) {
        inline = document.createElement('span');
        inline.setAttribute('data-b2b-hint', 'inline');
        label.appendChild(inline);
      }
      if (inline.textContent !== ' — ' + text) inline.textContent = ' — ' + text;
    }

    const container = input.closest('[class*="_formFieldContainer_"]');
    if (!container || !container.parentNode) return;

    let above = form.querySelector('[data-b2b-hint="above"]');
    if (above && above.nextElementSibling !== container) {
      above.remove();
      above = null;
    }
    if (!above) {
      above = document.createElement('div');
      above.setAttribute('data-b2b-hint', 'above');
      container.parentNode.insertBefore(above, container);
    }
    // On its own line it starts its own sentence, not one after the label.
    const sentence = text.charAt(0).toUpperCase() + text.slice(1);
    if (above.textContent !== sentence) above.textContent = sentence;
  }

  /* Validation ----------------------------------------------------------- */

  var enhanced = new WeakSet();
  var guarded = new WeakSet();

  /* The app labels its inputs through aria-labelledby, not <label for>. */
  function labelOf(root, input) {
    var id = input.getAttribute('aria-labelledby');
    var label = id && root.getElementById ? root.getElementById(id) : null;

    if (!label) {
      var container = input.closest('[class*="_formFieldContainer_"]');
      label = container && container.querySelector('label, [class*="_formInputFieldLabel_"]');
    }

    return normalise(label ? label.textContent : input.getAttribute('placeholder'));
  }

  function ruleFor(root, input) {
    var testid = input.getAttribute('data-testid');
    var label = labelOf(root, input);

    for (var i = 0; i < FIELD_RULES.length; i++) {
      var rule = FIELD_RULES[i];
      if (testid && rule.testids.indexOf(testid) !== -1) return rule;
      if (label && rule.labels.indexOf(label) !== -1) return rule;
    }

    return null;
  }

  /* React keeps the value in its own state, so a plain input.value = … is
     dropped on the next render. Go through the native setter and tell React. */
  function setValue(input, value) {
    var descriptor = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value');
    if (descriptor && descriptor.set) descriptor.set.call(input, value);
    else input.value = value;

    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  function showError(input, message) {
    var container = input.closest('[class*="_formFieldContainer_"]') || input;
    var parent = container.parentNode;
    if (!parent) return;

    var next = container.nextElementSibling;
    var node = next && next.hasAttribute('data-b2b-field-error') ? next : null;

    if (!message) {
      if (node) node.remove();
      input.removeAttribute('aria-invalid');
      return;
    }

    if (!node) {
      node = document.createElement('div');
      node.setAttribute('data-b2b-field-error', '');
      parent.insertBefore(node, container.nextSibling);
    }

    if (node.textContent !== message) node.textContent = message;
    input.setAttribute('aria-invalid', 'true');
  }

  function check(input, rule) {
    var value = input.value;

    if (rule.tidy) {
      var tidied = rule.tidy(value);
      if (tidied !== value) {
        setValue(input, tidied);
        value = tidied;
      }
    }

    var valid = value.trim() === '' || rule.test(value);
    showError(input, valid ? '' : t('validation', rule.key));
    return valid;
  }

  function enhanceFields(root) {
    var form = root.querySelector(FORM);
    if (!form) return;

    var inputs = form.querySelectorAll('input, textarea');

    Array.prototype.forEach.call(inputs, function (input) {
      var rule = ruleFor(root, input);
      if (!rule) return;

      Object.keys(rule.attributes).forEach(function (name) {
        if (input.getAttribute(name) !== rule.attributes[name]) {
          input.setAttribute(name, rule.attributes[name]);
        }
      });

      if (enhanced.has(input)) return;
      enhanced.add(input);

      input.addEventListener('blur', function () {
        check(input, rule);
      });

      input.addEventListener('input', function () {
        // Only while an error is on screen, so we do not shout at someone
        // halfway through typing their IBAN.
        var next = (input.closest('[class*="_formFieldContainer_"]') || input).nextElementSibling;
        if (next && next.hasAttribute('data-b2b-field-error')) check(input, rule);
      });
    });

    guard(form, root);
  }

  /* Stop a submit that carries a malformed field. Listening on the form in the
     capture phase puts us ahead of React's own handler on the app root. */
  function guard(form, root) {
    if (guarded.has(form)) return;
    guarded.add(form);

    var block = function (event) {
      var invalid = null;
      var inputs = form.querySelectorAll('input, textarea');

      Array.prototype.forEach.call(inputs, function (input) {
        var rule = ruleFor(root, input);
        if (rule && !check(input, rule) && !invalid) invalid = input;
      });

      if (!invalid) return;

      event.preventDefault();
      event.stopPropagation();
      invalid.focus();
    };

    form.addEventListener('submit', block, true);

    // Belt and braces: the widget may handle the button click instead of
    // firing a real submit event.
    form.addEventListener('click', function (event) {
      // Only the submit button — the country-code toggle is a <button> too,
      // and blocking that would break the phone field.
      var button = event.target.closest('[class*="_formSubmitButton_"], button[type="submit"]');
      if (button && !button.disabled) block(event);
    }, true);
  }

  /* Scanning ------------------------------------------------------------- */

  function scan(root) {
    muted = true;
    try {
      injectStyles(root);
      insertGroupHeadings(root);
      localiseText(root);
      insertHints(root);
      enhanceFields(root);
    } catch (error) {
      if (window.console) console.warn('[b2b-form]', error);
    } finally {
      muted = false;
    }

    // The app mounts its widget in a shadow root, sometimes nested.
    root.querySelectorAll('*').forEach(function (element) {
      if (element.shadowRoot) watch(element.shadowRoot);
    });
  }

  function watch(root) {
    if (seenRoots.has(root)) return;
    seenRoots.add(root);

    var queued = false;
    var rescan = function () {
      if (muted || queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        scan(root);
      });
    };

    scan(root);
    new MutationObserver(rescan).observe(root, { childList: true, subtree: true, characterData: true });
  }

  watch(document);
})();
